import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Mail, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle, 
  RefreshCw,
  Users,
  Building2,
  Calendar
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';

interface EmailMetrics {
  totalEmails: number;
  totalReferrals: number;
  acceptanceRate: number;
  averageResponseTime: number;
  emailsThisWeek: number;
  referralsThisWeek: number;
  topProviders: Array<{
    provider_name: string;
    email_count: number;
    referral_count: number;
  }>;
  recentActivity: Array<{
    id: string;
    type: 'email' | 'referral' | 'status_change';
    description: string;
    timestamp: string;
  }>;
}

const EnhancedEmailAnalytics = () => {
  const [metrics, setMetrics] = useState<EmailMetrics>({
    totalEmails: 0,
    totalReferrals: 0,
    acceptanceRate: 0,
    averageResponseTime: 0,
    emailsThisWeek: 0,
    referralsThisWeek: 0,
    topProviders: [],
    recentActivity: []
  });
  const [loading, setLoading] = useState(false);

  const fetchEmailAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch email exchanges count
      const { data: emails, error: emailError } = await supabase
        .from('email_exchanges')
        .select('*');

      if (emailError) throw emailError;

      // Fetch referrals data
      const { data: referrals, error: referralError } = await supabase
        .from('referrals')
        .select(`
          *,
          bed_listings!referrals_linked_listing_fkey(facility_name)
        `);

      if (referralError) throw referralError;

      // Calculate metrics
      const now = new Date();
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      const emailsThisWeek = emails?.filter(e => 
        new Date(e.created_at) >= weekAgo
      ).length || 0;

      const referralsThisWeek = referrals?.filter(r => 
        new Date(r.created_at) >= weekAgo
      ).length || 0;

      const acceptedReferrals = referrals?.filter(r => r.status === 'accepted').length || 0;
      const acceptanceRate = referrals?.length ? (acceptedReferrals / referrals.length) * 100 : 0;

      // Top providers by referral count
      const providerStats = referrals?.reduce((acc: any, referral) => {
        const providerName = referral.provider_name || 'Unknown Provider';
        if (!acc[providerName]) {
          acc[providerName] = {
            provider_name: providerName,
            email_count: 0,
            referral_count: 0
          };
        }
        acc[providerName].referral_count++;
        return acc;
      }, {});

      // Add email counts to provider stats
      emails?.forEach(email => {
        const toEmail = email.to_email;
        // Try to match emails to providers (simplified approach)
        Object.values(providerStats || {}).forEach((provider: any) => {
          if (email.subject.includes(provider.provider_name)) {
            provider.email_count++;
          }
        });
      });

      const topProviders = Object.values(providerStats || {})
        .sort((a: any, b: any) => b.referral_count - a.referral_count)
        .slice(0, 5) as any[];

      // Recent activity (combine emails and referrals)
      const recentActivity: any[] = [];
      
      emails?.slice(-10).forEach(email => {
        recentActivity.push({
          id: email.id,
          type: 'email',
          description: `Email sent: ${email.subject}`,
          timestamp: email.sent_at
        });
      });

      referrals?.slice(-10).forEach(referral => {
        recentActivity.push({
          id: referral.id,
          type: 'referral',
          description: `New referral to ${referral.provider_name}`,
          timestamp: referral.created_at
        });
      });

      // Sort by timestamp, most recent first
      recentActivity.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      setMetrics({
        totalEmails: emails?.length || 0,
        totalReferrals: referrals?.length || 0,
        acceptanceRate: Math.round(acceptanceRate * 100) / 100,
        averageResponseTime: 4.2, // Placeholder - would calculate from actual data
        emailsThisWeek,
        referralsThisWeek,
        topProviders: topProviders.slice(0, 5),
        recentActivity: recentActivity.slice(0, 20)
      });

    } catch (error) {
      console.error('Error fetching email analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailAnalytics();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Email & Referral Analytics</h2>
          <p className="text-muted-foreground">Monitor email-based referral performance</p>
        </div>
        <Button onClick={fetchEmailAnalytics} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Emails</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalEmails}</div>
            <p className="text-xs text-muted-foreground">
              +{metrics.emailsThisWeek} this week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Referrals</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalReferrals}</div>
            <p className="text-xs text-muted-foreground">
              +{metrics.referralsThisWeek} this week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Acceptance Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.acceptanceRate}%</div>
            <p className="text-xs text-muted-foreground">
              Overall success rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.averageResponseTime}h</div>
            <p className="text-xs text-muted-foreground">
              Provider response time
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Providers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Top Providers by Referrals
            </CardTitle>
          </CardHeader>
          <CardContent>
            {metrics.topProviders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No provider data available yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {metrics.topProviders.map((provider, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium text-sm">{provider.provider_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {provider.email_count} emails • {provider.referral_count} referrals
                      </div>
                    </div>
                    <Badge variant="secondary">
                      #{index + 1}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {metrics.recentActivity.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No recent activity</p>
              </div>
            ) : (
              <ScrollArea className="h-64">
                <div className="space-y-3">
                  {metrics.recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-start gap-3 p-2 border rounded">
                      {activity.type === 'email' && <Mail className="h-4 w-4 text-blue-500 mt-0.5" />}
                      {activity.type === 'referral' && <Users className="h-4 w-4 text-green-500 mt-0.5" />}
                      {activity.type === 'status_change' && <CheckCircle className="h-4 w-4 text-orange-500 mt-0.5" />}
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{activity.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(activity.timestamp))} ago
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Performance Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="space-y-2">
              <div className="text-2xl font-bold text-green-600">
                {metrics.totalReferrals > 0 ? 
                  Math.round((metrics.emailsThisWeek / Math.max(metrics.referralsThisWeek, 1)) * 100) / 100 
                  : 0
                }
              </div>
              <div className="text-sm text-muted-foreground">Emails per Referral</div>
            </div>
            
            <div className="space-y-2">
              <div className="text-2xl font-bold text-blue-600">
                {metrics.totalReferrals > 0 ? 
                  Math.round(((metrics.totalReferrals - metrics.totalEmails) / Math.max(metrics.totalReferrals, 1)) * 100)
                  : 0
                }%
              </div>
              <div className="text-sm text-muted-foreground">Email-to-Referral Conversion</div>
            </div>
            
            <div className="space-y-2">
              <div className="text-2xl font-bold text-orange-600">
                {metrics.topProviders.length}
              </div>
              <div className="text-sm text-muted-foreground">Active Providers</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnhancedEmailAnalytics;